import { useNavigate, useSearch } from "@tanstack/react-router";
import { useState } from "react";
import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import { useOutsourcesListQuery } from "../api/queries";
import { OutsourceBulkFromStaffDialog } from "../ui/OutsourceBulkFromStaffDialog";
import { OutsourceFilter } from "../ui/OutsourceFilter";
import { OutsourceTable } from "../ui/OutsourceTable";
import { OutsourceToolbar } from "../ui/OutsourceToolbar";

type OutsourcesSearch = {
  offset?: number;
  limit?: number;
  q?: string;
  categoryId?: string;
  payeeType?: "individual" | "team";
  status?: "active" | "inactive";
};

export function OutsourcesPage() {
  const nav = useNavigate({ from: "/app/performance/outsources" });
  const search: OutsourcesSearch = useSearch({ from: "/app/performance/outsources" });
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const offset = search.offset ?? 0;
  const limit = search.limit ?? 20;

  const list = useOutsourcesListQuery({
    q: search.q,
    categoryId: search.categoryId,
    payeeType: search.payeeType,
    status: search.status,
    limit,
    skip: offset,
  });
  const canCreate = useActionPermission(["performance-events:create"]);
  const canManageCategories = useActionPermission(["performance-events:update"]);

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            { label: "ແຜງຄວບຄຸມ", to: "/app/dashboard" },
            { label: "Outsource" },
          ]}
        />
      </Header>

      <Main>
        <OutsourceToolbar
          canManage={!!canCreate}
          onCreate={() => nav({ to: "/app/performance/outsources/create" })}
          onBulkFromStaff={() => setBulkDialogOpen(true)}
          onManageCategories={
            canManageCategories
              ? () =>
                  nav({
                    to: "/app/performance/settings/outsource-categories",
                  })
              : undefined
          }
          onManageApPlans={() =>
            nav({
              to: "/app/performance/settings/outsource-ap-installment-plans",
            })
          }
        />

        <div className="rounded-xl border bg-card pt-2">
          <OutsourceFilter />
          <OutsourceTable
            data={list.data?.data ?? []}
            isLoading={list.isLoading}
            offset={offset}
            limit={limit}
            totalCount={list.data?.meta?.total ?? 0}
            onPaginationChange={(nextOffset, nextLimit) =>
              nav({ search: { ...search, offset: nextOffset, limit: nextLimit } })
            }
            onEdit={(row) =>
              nav({
                to: "/app/performance/outsources/$id/edit",
                params: { id: row.id },
              })
            }
          />
        </div>

        <OutsourceBulkFromStaffDialog
          open={bulkDialogOpen}
          onOpenChange={setBulkDialogOpen}
          mode="master"
          existingDisplayNames={(list.data?.data ?? [])
            .filter((row) => row.isActive)
            .map((row) => row.displayName)}
        />
      </Main>
    </>
  );
}
